/**
 * Owner: Masa Bayu
 * File: AccountRoute.tsx
 * Date:Time: 2026-10-18 23:36
 * Description: §393 — gate akaun reader (login dahulu) → skrin ReaderProfileScreen
 *   tema QX baharu (ganti AccountPanel chrome lama). §468-D — peranan `admin`
 *   dibelek ke AdminAccountScreen: akaun operasi QX tidak memegang perpustakaan,
 *   pesanan atau hak bacaan, jadi chrome pembaca tidak pernah dilukis untuknya.
 */

import { useLayoutEffect } from 'react';
import { ReaderProfileScreen } from '../reader-profile/ReaderProfileScreen';
import { isHubAdmin, isHubSignedIn } from '../auth/session-auth';
import { AdminAccountScreen } from '../admin/AdminAccountScreen';
import type { AuthSessionView } from '../auth/session-types';
import { LoginPage } from './LoginPage';

export interface AccountRouteProps {
  readonly path?: string;
  readonly sessionReady: boolean;
  readonly signedIn: boolean;
  readonly signedInName: string | null;
  readonly authSession: AuthSessionView | null;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn: (session: AuthSessionView) => void;
  readonly onSignOut: () => void;
}

const ACCOUNT_LOGIN_SEARCH = 'returnTo=%2Faccount';

function accountLoginSearch(path: string): string {
  if (path.startsWith('/account/')) {
    return `returnTo=${encodeURIComponent(path)}`;
  }
  return ACCOUNT_LOGIN_SEARCH;
}

export function AccountRoute({
  path = '/account',
  sessionReady,
  signedIn,
  authSession,
  onNavigate,
  onSignedIn,
  onSignOut,
}: AccountRouteProps) {
  const allowed = signedIn && isHubSignedIn(authSession);
  const loginSearch = accountLoginSearch(path);

  useLayoutEffect(() => {
    if (sessionReady && !allowed) {
      onNavigate(`/login?${loginSearch}`);
    }
  }, [allowed, loginSearch, onNavigate, sessionReady]);

  if (!sessionReady) {
    return (
      <section className="login-page" aria-labelledby="account-gate-title">
        <h2 id="account-gate-title">Account</h2>
        <p className="login-page__lede">Checking session…</p>
      </section>
    );
  }

  if (!allowed || !authSession?.account) {
    return (
      <LoginPage
        search={loginSearch}
        signedInName={null}
        onNavigate={onNavigate}
        onSignedIn={onSignedIn}
        onSignOut={onSignOut}
      />
    );
  }

  return isHubAdmin(authSession) ? (
    <AdminAccountScreen authSession={authSession} onNavigate={onNavigate} onSignOut={onSignOut} />
  ) : (
    <ReaderProfileScreen
      path={path}
      authSession={authSession}
      onNavigate={onNavigate}
      onSignedIn={onSignedIn}
      onSignOut={onSignOut}
    />
  );
}
